import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Ship, MapPin, Phone, Mail } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const ShippingAgent = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  const [agents,         setAgents]         = useState([]);
  const [countries,      setCountries]      = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [showModal,      setShowModal]      = useState(false);
  const [editMode,       setEditMode]       = useState(false);
  const [submitLoading,  setSubmitLoading]  = useState(false);
  const [errors,         setErrors]         = useState({});

  const emptyForm = { ShippingAgentID: null, AgentName: '', ReferenceCode: '', ContactPerson: '', Address1: '', Address2: '', Address3: '', Phone: '', Fax: '', Email: '', WebSite: '', StatusActive: true, Remarks: '', CountryID: '' };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchCountries(); fetchAgents('all');
  }, [companyId, yearId]);

  useEffect(() => { if (companyId && yearId) fetchAgents(statusFilter); }, [statusFilter]);
  useEffect(() => { filterAgents(); }, [searchTerm, agents]);

  const fetchCountries = async () => {
    try {
      const res = await apiClient.get('/Countries.php');
      if (res.data.success) setCountries(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchAgents = async (status = 'all') => {
    setLoading(true);
    try {
      const res = await apiClient.get('/ShippingAgent.php', { params: { status } });
      if (res.data.success) setAgents(res.data.data || []);
      else alert('Failed to fetch shipping agents: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching shipping agents.');
    } finally { setLoading(false); }
  };

  const filterAgents = () => {
    if (!searchTerm.trim()) { setFilteredAgents(agents); return; }
    const q = searchTerm.toLowerCase();
    setFilteredAgents(agents.filter(a =>
      a.AgentName.toLowerCase().includes(q) || a.ReferenceCode?.toLowerCase().includes(q) || a.ContactPerson?.toLowerCase().includes(q)
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.AgentName.trim()) errs.AgentName = 'Agent name is required';
    if (formData.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) errs.Email = 'Invalid email format';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = { ...formData, StatusActive: formData.StatusActive ? 1 : 0, CountryID: formData.CountryID || null };
      const res = await apiClient[editMode ? 'put' : 'post']('/ShippingAgent.php', payload);
      if (res.data.success) {
        alert(editMode ? 'Shipping agent updated successfully!' : 'Shipping agent created successfully!');
        closeModal(); fetchAgents(statusFilter);
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving: ' + (err.response?.data?.message || err.message));
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = async (agent) => {
    try {
      const res = await apiClient.get(`/ShippingAgent.php?id=${agent.ShippingAgentID}`);
      if (res.data.success) {
        const d = res.data.data;
        setFormData({ ShippingAgentID: d.ShippingAgentID, AgentName: d.AgentName || '', ReferenceCode: d.ReferenceCode || '', ContactPerson: d.ContactPerson || '', Address1: d.Address1 || '', Address2: d.Address2 || '', Address3: d.Address3 || '', Phone: d.Phone || '', Fax: d.Fax || '', Email: d.Email || '', WebSite: d.WebSite || '', Remarks: d.Remarks || '', CountryID: d.CountryID || '', StatusActive: Number(d.StatusActive) === 1 });
        setEditMode(true); setShowModal(true); setErrors({});
      }
    } catch { alert('Error loading shipping agent details'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shipping agent?')) return;
    try {
      const res = await apiClient.delete('/ShippingAgent.php', { data: { ShippingAgentID: id } });
      if (res.data.success) { alert('Shipping agent deleted successfully!'); fetchAgents(statusFilter); }
      else alert('Error: ' + res.data.message);
    } catch (err) { alert('Error deleting: ' + (err.response?.data?.message || 'Please try again.')); }
  };

  const resetForm    = () => { setFormData(emptyForm); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const set = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  const inputCls  = (err) => `w-full px-3.5 py-2.5 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = `w-full px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)]`;
  const labelCls  = 'block mb-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide';
  const errMsg    = (msg) => msg ? <p className="text-[11px] text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  const SectionLabel = ({ icon: Icon, title }) => (
    <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide
      px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-4"
      style={{ borderLeftColor: NAVY }}>
      {Icon && <Icon size={13} />} {title}
    </div>
  );

  // ── no session ─────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <Ship size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Ship size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Shipping Agent Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage shipping agents and carriers</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Shipping Agent
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by agent name, code, or contact..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b]">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredAgents.length} {filteredAgents.length === 1 ? 'agent' : 'agents'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading shipping agents...</div>
        ) : filteredAgents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Ship size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No shipping agents found</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear search</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Ref Code', 'Agent Name', 'Contact Person', 'Phone', 'Email', 'Country', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAgents.map(agent => (
                  <tr key={agent.ShippingAgentID}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                        {agent.ReferenceCode || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{agent.AgentName}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">{agent.ContactPerson || '—'}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Phone size={12} className="text-gray-400 flex-shrink-0" />
                        {agent.Phone || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Mail size={12} className="text-gray-400 flex-shrink-0" />
                        {agent.Email || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">{agent.countryname || '—'}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        Number(agent.StatusActive) === 1
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-400'
                          : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {Number(agent.StatusActive) === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(agent)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(agent.ShippingAgentID)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[720px] shadow-2xl border border-gray-200 overflow-hidden max-h-[92vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Ship size={18} /> {editMode ? 'Edit Shipping Agent' : 'Add New Shipping Agent'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7">

              {/* ── Basic Info ── */}
              <SectionLabel icon={Ship} title="Basic Information" />
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className={labelCls}>Agent Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.AgentName} onChange={e => set('AgentName', e.target.value)}
                    maxLength="100" placeholder="e.g., Maersk Line" autoFocus className={inputCls(errors.AgentName)} />
                  {errMsg(errors.AgentName)}
                </div>
                <div>
                  <label className={labelCls}>Reference Code</label>
                  <input type="text" value={formData.ReferenceCode} onChange={e => set('ReferenceCode', e.target.value.toUpperCase())}
                    maxLength="50" placeholder="e.g., MAERSK" className={inputCls(false)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className={labelCls}>Contact Person</label>
                  <input type="text" value={formData.ContactPerson} onChange={e => set('ContactPerson', e.target.value)}
                    maxLength="100" placeholder="e.g., John Smith" className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Country</label>
                  <select value={formData.CountryID} onChange={e => set('CountryID', e.target.value)} className={selectCls}>
                    <option value="">— Select Country —</option>
                    {countries.map(c => <option key={c.countryid} value={c.countryid}>{c.countryname}</option>)}
                  </select>
                </div>
              </div>

              {/* ── Address ── */}
              <SectionLabel icon={MapPin} title="Address Information" />
              <div className="mb-3">
                <label className={labelCls}>Address Line 1</label>
                <input type="text" value={formData.Address1} onChange={e => set('Address1', e.target.value)}
                  maxLength="100" placeholder="Street address" className={inputCls(false)} />
              </div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className={labelCls}>Address Line 2</label>
                  <input type="text" value={formData.Address2} onChange={e => set('Address2', e.target.value)}
                    maxLength="100" placeholder="Apartment, suite" className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Address Line 3</label>
                  <input type="text" value={formData.Address3} onChange={e => set('Address3', e.target.value)}
                    maxLength="100" placeholder="Additional info" className={inputCls(false)} />
                </div>
              </div>

              {/* ── Contact ── */}
              <SectionLabel icon={Phone} title="Contact Information" />
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className={labelCls}>Phone</label>
                  <input type="text" value={formData.Phone} onChange={e => set('Phone', e.target.value)}
                    maxLength="50" placeholder="+971-4-1234567" className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Fax</label>
                  <input type="text" value={formData.Fax} onChange={e => set('Fax', e.target.value)}
                    maxLength="50" placeholder="+971-4-7654321" className={inputCls(false)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={formData.Email} onChange={e => set('Email', e.target.value)}
                    maxLength="100" placeholder="info@agent.com" className={inputCls(errors.Email)} />
                  {errMsg(errors.Email)}
                </div>
                <div>
                  <label className={labelCls}>Website</label>
                  <input type="text" value={formData.WebSite} onChange={e => set('WebSite', e.target.value)}
                    maxLength="100" placeholder="www.agent.com" className={inputCls(false)} />
                </div>
              </div>

              {/* ── Status & Remarks ── */}
              <SectionLabel title="Status & Remarks" />
              <div className="mb-3">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150 w-fit">
                  <input type="checkbox" checked={!!formData.StatusActive} onChange={e => set('StatusActive', e.target.checked)}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>
              <div className="mb-6">
                <label className={labelCls}>Remarks</label>
                <textarea value={formData.Remarks} onChange={e => set('Remarks', e.target.value)}
                  rows={3} maxLength={200} placeholder="Additional notes or comments..."
                  className="w-full px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] resize-y font-[inherit]" />
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default ShippingAgent;